/**
 * Owns every piece of the merged table's availability columns
 * (offer-unified-table, CU-86cbc3r8b): the show/hide + granularity
 * preference, the unit-scoped full-range fetch, the rich column build,
 * rectangle multi-select, the INSPECT popover
 * (reserve/upgrade/close-sale/cancel/force-release + waitlist), and the TTL
 * countdown. Scoped to the offer being viewed — no offer picker.
 *
 * The three concerns named above (availability columns, multi-select,
 * INSPECT/queue wiring) each live in their own sibling hook — see
 * `use-offer-availability-data.ts`, `use-offer-table-multi-select.ts`, and
 * `use-offer-table-inspect-queue.tsx`. This file is only the integration
 * point that wires their outputs into one `ColumnDef[]` build and one
 * flattened public API.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { BatchReleaseResponse, BatchReserveResponse } from '@ooh/contracts';
import type { ColumnDef } from '@ooh/ui/components/data-table';
import {
  buildQueueJoinPanelLabels,
  GRANULARITY_COL_WIDTH,
  useQueueClientOptions,
  useSelectQueueJoin,
  type MultiCellSelection,
  type MultiSelectCellRef,
  useQueueOfferOptions,
} from '@ooh/screens';
import type { UnifiedSelectCellResolution } from '@ooh/screens';
import { buildOfferAvailabilityColumns } from '../lib/offer-availability-columns';
import type { OfferUnitItem } from '../units-tab-columns';
import { useOfferAvailabilityData } from './use-offer-availability-data';
import { useOfferTableMultiSelect } from './use-offer-table-multi-select';
import { useOfferTableInspectQueue } from './use-offer-table-inspect-queue';
import type { useOfferTableAvailabilityPreferences } from './use-offer-table-availability-preferences';

export interface UseOfferTableAvailabilityOptions {
  offerId: number;
  offerLabel: string;
  canEdit: boolean;
  /** Rows currently loaded in the merged table (the availability fetch scope). */
  rows: OfferUnitItem[];
  /** Offer's own campaign dates — `null` renders the "set dates" explanation instead of columns. */
  defaultDateFrom: string | null;
  defaultDateTo: string | null;
  onOpenCampaignDatesEditor?: () => void;
  /**
   * Forces availability visible for this render only, without touching the
   * persisted preference — the retired `?tab=availability` address's
   * one-visit column reveal (offer-unified-table: "A retired address
   * reveals the columns it was saved for").
   */
  forceShow?: boolean;
}

export interface UseOfferTableAvailabilityResult {
  showAvailability: boolean;
  toggleAvailability: () => void;
  granularity: ReturnType<typeof useOfferTableAvailabilityPreferences>['granularity'];
  setGranularity: (value: UseOfferTableAvailabilityResult['granularity']) => void;
  columns: ColumnDef<OfferUnitItem>[];
  isLoading: boolean;
  isError: boolean;
  /** True while availability is shown but the offer has no campaign dates — render the "set dates" explanation instead of the columns. */
  showsNoDatesExplanation: boolean;
  multiSelection: MultiCellSelection | null;
  multiSelectAnchorRef: React.RefObject<HTMLElement | null>;
  clearMultiSelection: () => void;
  resolveMultiSelectCell: (ref: MultiSelectCellRef) => UnifiedSelectCellResolution | null;
  handleBatchReserveSuccess: (response: BatchReserveResponse) => void;
  handleBatchReleaseSuccess: (response: BatchReleaseResponse) => void;
  /** Offer-unit ids among `rows` that carry a reservation held by this offer — for the reservation-aware remove confirmation. */
  reservedUnitIds: Set<OfferUnitItem['id']>;
  /**
   * The wired `QueueJoinPanel` node ("Dodaj wpis dla innego klienta", CU-86cbejhae) for
   * the currently selected single own-held cell — passed straight through to
   * `UnifiedSelectPopover`'s `queueJoinSection` prop. `undefined` when the selection
   * isn't exactly one own-held (reserved-own/option-own) cell.
   */
  queueJoinSection: React.ReactNode;
  /** Passed straight through to `UnifiedSelectPopover`'s `queueJoinLoading` prop (CU-86cbejhae). */
  queueJoinLoading: boolean;
}

export function useOfferTableAvailability({
  offerId,
  offerLabel,
  canEdit,
  rows,
  defaultDateFrom,
  defaultDateTo,
  onOpenCampaignDatesEditor: _onOpenCampaignDatesEditor,
  forceShow = false,
}: UseOfferTableAvailabilityOptions): UseOfferTableAvailabilityResult {
  // Same key the catalog's own `buildRichAvailabilityColumns` caller uses
  // (`routes/media-units/index.tsx`) — the tooltip's "Wygasa"/"Expires" deadline
  // prefix lives in the `inventory` namespace, not duplicated per-surface.
  const { t: tInventory } = useTranslation('inventory');
  const { t: tReservations } = useTranslation('reservations');

  const data = useOfferAvailabilityData({
    offerId,
    rows,
    defaultDateFrom,
    defaultDateTo,
    forceShow,
  });

  const inspectQueue = useOfferTableInspectQueue({
    offerId,
    offerLabel,
    canEdit,
    periods: data.periods,
    units: data.availabilityQuery.units,
    mediaUnitIdById: data.mediaUnitIdById,
    refetchAvailability: data.availabilityQuery.refetch,
  });

  const multiSelect = useOfferTableMultiSelect({
    canSelect: canEdit && inspectQueue.canCreateReservation,
    rows,
    periods: data.periods,
    entriesGrid: data.entriesGrid,
    gridRows: data.gridRows,
    grid: data.grid,
    mediaUnitIdById: data.mediaUnitIdById,
    refetchAvailability: data.availabilityQuery.refetch,
  });

  const canSelect = canEdit && inspectQueue.canCreateReservation;

  const selectQueueClientOptions = useQueueClientOptions();
  const selectQueueOfferOptions = useQueueOfferOptions();
  const selectQueueJoinPanelLabels = React.useMemo(
    () =>
      buildQueueJoinPanelLabels(
        tReservations as (key: string, options?: Record<string, unknown>) => string,
      ),
    [tReservations],
  );
  const { queueJoinSection, queueJoinLoading } = useSelectQueueJoin({
    selection: multiSelect.multiSelection,
    resolveCell: multiSelect.resolveMultiSelectCell,
    testId: 'units-tab-select-queue',
    clientOptions: selectQueueClientOptions,
    offerOptions: selectQueueOfferOptions,
    labels: selectQueueJoinPanelLabels,
  });

  const columns = React.useMemo<ColumnDef<OfferUnitItem>[]>(() => {
    if (!data.effectiveShowAvailability || !data.hasCampaignDates || data.periods.length === 0)
      return [];
    return buildOfferAvailabilityColumns({
      periods: data.periods,
      rowIds: data.rowIds,
      occupancyByRowId: data.occupancyByRowId,
      colWidth: GRANULARITY_COL_WIDTH[data.preferences.granularity],
      granularity: data.preferences.granularity,
      expiresLabelPrefix: tInventory('availability.inspect.expiresLabel'),
      openCellKey: inspectQueue.openInspectCellKey,
      onOpenCellKeyChange: inspectQueue.setOpenInspectCellKey,
      onCellClick: canSelect ? multiSelect.handleCellClick : undefined,
      onCellMouseDown: canSelect ? multiSelect.handleCellMouseDown : undefined,
      onCellMouseEnter: canSelect ? multiSelect.handleCellMouseEnter : undefined,
      onCellMouseUp: canSelect ? multiSelect.handleCellMouseUp : undefined,
      isCellSelected: (rowId, periodId) =>
        multiSelect.multiSelection?.cells.some(
          (c) => c.unitId === rowId && c.periodId === periodId,
        ) ?? false,
      renderPopoverContent: inspectQueue.renderInspectContent,
    });
  }, [
    data.effectiveShowAvailability,
    data.preferences.granularity,
    data.hasCampaignDates,
    data.periods,
    data.rowIds,
    data.occupancyByRowId,
    inspectQueue.openInspectCellKey,
    inspectQueue.setOpenInspectCellKey,
    inspectQueue.renderInspectContent,
    canSelect,
    multiSelect.handleCellClick,
    multiSelect.handleCellMouseDown,
    multiSelect.handleCellMouseEnter,
    multiSelect.handleCellMouseUp,
    multiSelect.multiSelection,
    tInventory,
  ]);

  return {
    // Reflects what is actually shown (forced or persisted) so the toggle
    // button's pressed state matches the columns actually rendered — a
    // retired `?tab=availability` reveal looks "on" for this visit even
    // when the persisted preference is off.
    showAvailability: data.effectiveShowAvailability,
    toggleAvailability: () =>
      data.preferences.setShowAvailability(!data.preferences.showAvailability),
    granularity: data.preferences.granularity,
    setGranularity: data.preferences.setGranularity,
    columns,
    isLoading: data.availabilityQuery.isLoading,
    isError: data.availabilityQuery.isError,
    showsNoDatesExplanation: data.effectiveShowAvailability && !data.hasCampaignDates,
    multiSelection: multiSelect.multiSelection,
    multiSelectAnchorRef: multiSelect.multiSelectAnchorRef,
    clearMultiSelection: multiSelect.clearMultiSelection,
    resolveMultiSelectCell: multiSelect.resolveMultiSelectCell,
    handleBatchReserveSuccess: multiSelect.handleBatchReserveSuccess,
    handleBatchReleaseSuccess: multiSelect.handleBatchReleaseSuccess,
    reservedUnitIds: data.reservedUnitIds,
    queueJoinSection,
    queueJoinLoading,
  };
}
